import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash, UserPlus, X } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import UserModal from '../components/modals/UserModal';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/ui/ConfirmModal';
import { SkeletonTable } from '../components/ui/Skeleton';
import StandardTable from '../components/ui/StandardTable';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [permissionsCatalog, setPermissionsCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToToggleStatus, setUserToToggleStatus] = useState(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const toast = useToast();
    const { checkPermission } = useAuth();

    const canManageUsers = checkPermission('users:write');

    useEffect(() => {
        fetchUsers();
        fetchPermissionCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await api.getUsers();
            setUsers(data.users || []);
        } catch {
            toast.error('Erro ao buscar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissionCatalog = async () => {
        try {
            const data = await api.getPermissions();
            setPermissionsCatalog(data || []);
        } catch {
            setPermissionsCatalog([]);
        }
    };

    const handleEdit = useCallback(
        (user) => {
            if (!canManageUsers) {
                toast.error('Voce nao tem permissao para editar usuarios');
                return;
            }
            setSelectedUser(user);
            setShowModal(true);
        },
        [canManageUsers, toast],
    );

    const handleCreate = () => {
        if (!canManageUsers) {
            toast.error('Voce nao tem permissao para criar usuarios');
            return;
        }
        setSelectedUser(null);
        setShowModal(true);
    };

    const handleDelete = useCallback(
        (user) => {
            if (!canManageUsers) {
                toast.error('Voce nao tem permissao para excluir usuarios');
                return;
            }
            setUserToDelete(user);
        },
        [canManageUsers, toast],
    );

    const confirmDelete = async () => {
        if (!userToDelete) {
            return;
        }

        setConfirmLoading(true);
        try {
            await api.deleteUser(userToDelete.id);
            toast.success('Usuario excluido com sucesso');
            await fetchUsers();
        } catch (error) {
            if (error.status === 404) {
                toast.info('Usuario ja foi removido ou nao existe mais');
                await fetchUsers();
                return;
            }
            toast.error(error.message || 'Erro ao excluir usuario');
        } finally {
            setConfirmLoading(false);
            setUserToDelete(null);
        }
    };

    const handleToggleStatus = useCallback(
        (user) => {
            if (!canManageUsers) {
                toast.error('Voce nao tem permissao para alterar o status de usuarios');
                return;
            }
            setUserToToggleStatus(user);
        },
        [canManageUsers, toast],
    );

    const confirmToggleStatus = async () => {
        if (!userToToggleStatus) {
            return;
        }

        const action = userToToggleStatus.is_active ? 'desativar' : 'ativar';

        setConfirmLoading(true);
        try {
            await api.updateUser(userToToggleStatus.id, {
                is_active: !userToToggleStatus.is_active,
            });
            toast.success(`Usuario ${userToToggleStatus.is_active ? 'desativado' : 'ativado'} com sucesso`);
            await fetchUsers();
        } catch (error) {
            toast.error(error.message || `Erro ao ${action} usuario`);
        } finally {
            setConfirmLoading(false);
            setUserToToggleStatus(null);
        }
    };

    const renderPermissionBadges = (user) => {
        const permissions = Array.isArray(user.permissions) ? user.permissions : [];
        if (permissions.length === 0) {
            return <Badge variant="warning">Sem permissao</Badge>;
        }

        const visible = permissions.slice(0, 4);
        const remaining = permissions.length - visible.length;

        return (
            <div className="flex flex-wrap gap-2">
                {visible.map((permission) => (
                    <Badge key={`${user.id}-${permission}`}>{permission}</Badge>
                ))}
                {remaining > 0 ? <Badge variant="secondary">+{remaining}</Badge> : null}
            </div>
        );
    };

    const columns = useMemo(
        () => [
            {
                key: 'full_name',
                label: 'Nome',
                getValue: (user) => user.full_name,
                render: (user) => <span className="font-medium text-black dark:text-white">{user.full_name}</span>,
            },
            {
                key: 'email',
                label: 'E-mail',
                getValue: (user) => user.email,
            },
            {
                key: 'organization_name',
                label: 'Organizacao',
                getValue: (user) => user.organization_name || user.organization_id || '-',
                render: (user) => (
                    <span className="text-sm text-mono-700 dark:text-mono-300">
                        {user.organization_name || user.organization_id || '-'}
                    </span>
                ),
            },
            {
                key: 'permissions',
                label: 'Permissoes',
                getValue: (user) => (Array.isArray(user.permissions) ? user.permissions.join(' | ') : ''),
                render: (user) => renderPermissionBadges(user),
            },
            {
                key: 'is_active',
                label: 'Status',
                getValue: (user) => (user.is_active ? 1 : 0),
                render: (user) =>
                    user.is_active ? <Badge variant="success">Ativo</Badge> : <Badge variant="danger">Inativo</Badge>,
            },
            {
                key: 'actions',
                label: 'Acoes',
                sortable: false,
                searchable: false,
                getValue: () => '',
                render: (user) =>
                    canManageUsers ? (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleEdit(user);
                                }}
                            >
                                Editar
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleToggleStatus(user);
                                }}
                                className={`${user.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'} px-2`}
                                title={user.is_active ? 'Desativar usuario' : 'Ativar usuario'}
                            >
                                {user.is_active ? (
                                    <>
                                        <X size={14} className="mr-1" />
                                        Desativar
                                    </>
                                ) : (
                                    <>
                                        <UserPlus size={14} className="mr-1" />
                                        Ativar
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleDelete(user);
                                }}
                                className="px-2"
                                title="Excluir usuario permanentemente"
                            >
                                <Trash size={14} className="mr-1" />
                                Excluir
                            </Button>
                        </div>
                    ) : (
                        <span className="text-xs text-mono-500">Somente leitura</span>
                    ),
            },
        ],
        [canManageUsers, handleDelete, handleEdit, handleToggleStatus],
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <PageHeader title="Gerenciamento de usuarios" subtitle="Controle de contas e permissoes de acesso" />
                <SkeletonTable columns={5} rows={10} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Gerenciamento de usuarios"
                subtitle="Controle de contas e permissoes de acesso"
                actions={
                    canManageUsers ? (
                        <Button onClick={handleCreate} className="w-full sm:w-auto">
                            <Plus size={20} className="mr-2" />
                            Criar usuario
                        </Button>
                    ) : null
                }
            />

            {canManageUsers ? (
                <>
                <UserModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onSuccess={() => {
                        fetchUsers();
                        toast.success(selectedUser ? 'Usuario atualizado' : 'Usuario criado');
                    }}
                    user={selectedUser}
                    permissionsCatalog={permissionsCatalog}
                />
                </>
            ) : null}

            <ConfirmModal
                isOpen={Boolean(userToDelete)}
                onClose={() => setUserToDelete(null)}
                onConfirm={confirmDelete}
                title="Excluir usuario"
                message={
                    userToDelete
                        ? `Tem certeza que deseja excluir permanentemente o usuario "${userToDelete.full_name}"?`
                        : ''
                }
                confirmText="Excluir usuario"
                confirmVariant="danger"
                loading={confirmLoading}
            />

            <ConfirmModal
                isOpen={Boolean(userToToggleStatus)}
                onClose={() => setUserToToggleStatus(null)}
                onConfirm={confirmToggleStatus}
                title={userToToggleStatus?.is_active ? 'Desativar usuario' : 'Ativar usuario'}
                message={
                    userToToggleStatus
                        ? `Confirma ${userToToggleStatus.is_active ? 'desativar' : 'ativar'} o usuario "${userToToggleStatus.full_name}"?`
                        : ''
                }
                confirmText={userToToggleStatus?.is_active ? 'Desativar' : 'Ativar'}
                loading={confirmLoading}
            />

            <StandardTable
                rows={users}
                columns={columns}
                rowKey={(row) => row.id}
                onRowClick={(row) => navigate(`/users/${row.id}`)}
                exportEnabled
                exportFileName="usuarios"
                emptyMessage="Nenhum usuario encontrado."
                searchPlaceholder="Pesquisar por nome, email ou permissao"
            />
        </div>
    );
};

export default Users;
